
import React from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchStatistics } from "@/services/adminService";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Users, Beaker, RefreshCw, Wallet, MessageSquare, ArrowRightLeft, PackageCheck } from "lucide-react";

const AdminStats = () => {
  const { data: stats, isLoading, error } = useQuery({
    queryKey: ["admin-statistics"],
    queryFn: fetchStatistics,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes to avoid excessive database queries
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
    retry: 1, // Only retry once if there's an error
  });

  if (isLoading) {
    return (
      <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
        {[1, 2, 3, 4, 5, 6, 7].map((i) => (
          <Card key={i} className="md:col-span-1">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <Skeleton className="h-12 w-12 rounded-full" />
                <div>
                  <Skeleton className="h-5 w-[120px] mb-2" />
                  <Skeleton className="h-8 w-[80px]" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-100 text-red-600 p-4 rounded-md mb-6">
        Failed to load statistics. Please try refreshing the page.
      </div>
    );
  }

  const statItems = [
    {
      title: "Total Users",
      value: stats?.total_users || 0,
      icon: <Users className="h-6 w-6 text-blue-500" />,
      color: "bg-blue-50",
    },
    {
      title: "Total Reagents",
      value: stats?.total_reagents || 0,
      icon: <Beaker className="h-6 w-6 text-green-500" />,
      color: "bg-green-50",
    },
    {
      title: "Completed Swaps",
      value: stats?.total_swaps_completed || 0,
      icon: <RefreshCw className="h-6 w-6 text-purple-500" />,
      color: "bg-purple-50",
    },
    {
      title: "Credits Exchanged",
      value: stats?.total_credits_exchanged || 0,
      icon: <Wallet className="h-6 w-6 text-amber-500" />,
      color: "bg-amber-50",
    },
    {
      title: "Messages Sent",
      value: stats?.total_messages_sent || 0,
      icon: <MessageSquare className="h-6 w-6 text-indigo-500" />,
      color: "bg-indigo-50",
    },
    {
      title: "Total Swap Requests",
      value: stats?.total_swap_requests || 0,
      icon: <ArrowRightLeft className="h-6 w-6 text-rose-500" />,
      color: "bg-rose-50",
    },
    {
      title: "Reagent Swaps",
      value: stats?.total_reagent_swaps || 0,
      icon: <PackageCheck className="h-6 w-6 text-teal-500" />,
      color: "bg-teal-50",
    },
  ];

  return (
    <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
      {statItems.map((item) => (
        <Card key={item.title} className="md:col-span-1">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className={`${item.color} p-3 rounded-full`}>
                {item.icon}
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {item.title}
                </p>
                <h3 className="text-2xl font-bold">{item.value.toLocaleString()}</h3>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default AdminStats;
